import { useCallback, useMemo } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { useSites } from 'store'
import { useSort, useFilters } from './store'

export interface ISIP {
  type: string
  room: string
  description: string
  user: string
  user_email: string
  phone_short: string
  phone_long: string
  alarm?: IAlarm
  calling_profile: string
  status: string
  last_connection: string
  last_phone_model: string
}

export interface IAlarm {
  start_date: string
  end_date: string
  timezone: string
  alarm_time: string
  status: string
  last_sync_error?: string
  created_by: {
    email: string
    first_name: string
    last_name: string
  }
  updated_by: {
    email: string
    first_name: string
    last_name: string
  }
}

export const OPERATION_SIPS = gql`
  query VoipSIPS($siteSfId: ID!) {
    sips: voipSIPs(site_sf_id: $siteSfId) {
      type
      room
      description
      user
      user_email
      phone_short
      phone_long
      calling_profile
      status
      last_connection
      last_phone_model
      alarm {
        status
        start_date
        end_date
        timezone
        alarm_time
        created_by {
          email
          first_name
          last_name
        }
        updated_by {
          email
          first_name
          last_name
        }
      }
    }
  }
`

const CALLING_PROFILES = ['default', 'internal', 'national', 'national_full', 'international']

const EMPTY_PREFERENCES = CALLING_PROFILES.reduce((acc, key) => ({ ...acc, [key]: 0 }), {})
const EMPTY_TYPES = { administrative: 0, room: 0 }

export const useSIPs = () => {
  const { t } = useTranslation()
  const [{ selected }] = useSites()
  const [filters] = useFilters()
  const [sort] = useSort()
  const { data, loading, error } = useQuery(OPERATION_SIPS, {
    variables: {
      siteSfId: selected[0].sf_id
    },
    fetchPolicy: 'network-only'
  })

  const statuses = useMemo(() => {
    return data?.sips?.reduce(
      (acc, sip) => {
        return {
          up: sip.status === 'up' && filters.type === sip.type ? acc.up + 1 : acc.up,
          down: sip.status === 'down' && filters.type === sip.type ? acc.down + 1 : acc.down
        }
      },
      { up: 0, down: 0 }
    )
  }, [data, filters])

  const types = useMemo(() => {
    if (!data) {
      return EMPTY_TYPES
    }
    return data?.sips?.reduce(
      (acc, sip) => ({
        administrative: sip.type === 'administrative' ? acc.administrative + 1 : acc.administrative,
        room: sip.type === 'room' ? acc.room + 1 : acc.room
      }),
      { ...EMPTY_TYPES }
    )
  }, [data])

  const preferences = useMemo(() => {
    if (!data) {
      return EMPTY_PREFERENCES
    }
    return data?.sips?.reduce(
      (acc, sip) => {
        if (filters.type === sip.type && CALLING_PROFILES.includes(sip.calling_profile)) {
          return {
            ...acc,
            [sip.calling_profile]: acc[sip.calling_profile] + 1
          }
        }
        return acc
      },
      { ...EMPTY_PREFERENCES }
    )
  }, [data, filters])
  const sips = useMemo(() => {
    return (data?.sips || [])
      .filter(sip => {
        if (filters.statuses.length === 1) {
          return sip.status === filters.statuses[0]
        }
        return sip
      })
      .filter(sip => sip.type === filters.type)
      .filter(sip => {
        if (filters.preferences.length) {
          console.log('1', sip.calling_profile)
          return filters.preferences.includes(sip.calling_profile)
        }
        return sip
      })
      .filter(sip => {
        if (!filters.verbatim.length) {
          return sip
        }
        return (
          (sip.phone_short || '').toLowerCase().indexOf(filters.verbatim.toLowerCase()) > -1 ||
          (sip.phone_long || '').toLowerCase().indexOf(filters.verbatim.toLowerCase()) > -1 ||
          (sip.user || '').toLowerCase().indexOf(filters.verbatim.toLowerCase()) > -1
        )
      })
  }, [data, filters])
  const selection = useMemo(() => {
    return [...sips].sort((a, b) => {
      if (sort.by === 'last_connection') {
        const aIsUp = a.status === 'up'
        const bIsUp = b.status === 'up'

        if (aIsUp && bIsUp) return 0
        if (aIsUp) return sort.direction === 'asc' ? 1 : -1
        if (bIsUp) return sort.direction === 'asc' ? -1 : 1

        if (!a[sort.by] && !b[sort.by]) return 0
        if (!a[sort.by]) return 1
        if (!b[sort.by]) return -1

        const comparison = new Date(a[sort.by]).getTime() - new Date(b[sort.by]).getTime()
        return sort.direction === 'asc' ? comparison : -comparison
      }
      return sort.direction === 'asc'
        ? (a[sort.by] || '').localeCompare(b[sort.by] || '')
        : (b[sort.by] || '').localeCompare(a[sort.by] || '')
    })
  }, [sips, sort])

  const handleExport = useCallback(() => {
    const header = [
      t('voip.sip.status'),
      t('voip.sip.type'),
      t('voip.sip.user'),
      t('voip.sip.phone_long'),
      t('voip.sip.phone_short'),
      t('voip.sip.calling_profile_label'),
      t('voip.sip.last_connection')
    ].join(';')
    const body = sips.map(sip =>
      [
        t(`voip.sip.status_${sip.status}`),
        sip.type === 'administrative' ? 'Admin.' : t('voip.sip.room'),
        sip.user || '-',
        sip.phone_long,
        sip.phone_short,
        t(`voip.sip.calling_profile.${sip.calling_profile}`),
        sip.status === 'down'
          ? sip.last_connection
            ? t('voip.datetime', {
                date: new Date(sip.last_connection).toLocaleDateString(),
                time: new Date(sip.last_connection).toLocaleTimeString()
              })
            : '-'
          : t('voip.sip.online')
      ].join(';')
    )
    return `data:text/csv;charset=utf-8,${[header, ...body].join('\n')}`
  }, [sips, t])

  return {
    loading,
    error,
    statuses,
    types,
    preferences,
    sips: selection,
    onExport: handleExport
  }
}
